import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closePaymentMethod,
  closePaymentModal,
  openPaymentCompletionModal,
} from "../../../features/slices/ModalSlice";
import { useMediaQuery } from "react-responsive";
import { updateSelectedItem } from "../../../features/slices/CommonSlice";
import ActionButtons from "./ActionButtons";
import { submitDeposit } from "../../../features/slices/PaymentSlice";
import ErrorCard from "../../common/ErrorCard";
import UtrReceiptFields, {
  isValidReceipt,
  isValidUtr,
} from "./UtrReceiptFields";
import copyIcon from "../../../assets/images/copy.svg";
import { openInNewTab } from "../../../utils/openInNewTab";
export default function UPIPayment() {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [referenceNumber, setReferenceNumber] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [redBorder, setRedBorder] = useState(false);
  const [tos, setTos] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const modal = useSelector((state) => state.modal);
  const payment = useSelector((state) => state.payment);
  const [checkValidation, setValidationRemider] = useState(false);
  const dispatch = useDispatch();
  const timer = useRef(null);

  // const closeModal = () => {
  //   dispatch(closePaymentMethod());
  // };
  const isUtrValid = isValidUtr(referenceNumber);
  const canSubmit = isUtrValid && tos && isValidReceipt(receipt);

  const selectedMethod =
    payment.paymentMethods.find(
      (item) => item.id === modal.depositData?.paymentMethodId
    ) || {};
  const selectedMethodDetails = selectedMethod.details || {};
  const upiId = selectedMethodDetails.upiId || "";
  const qrCodeUrl = selectedMethodDetails.qrCodeUrl || "";
  // a missing or broken QR image (e.g. an expired signed link) falls back to
  // paying by UPI ID instead of showing a broken image
  const [qrFailed, setQrFailed] = useState(false);
  const showQr = Boolean(qrCodeUrl) && !qrFailed;
  const isManualType =
    String(selectedMethod.type || "").toLowerCase() === "manual";

  const goToNextStep = (event) => {
    event.preventDefault();
    if (!isUtrValid) {
      setRedBorder(true);
      if (isMobile) {
        const section = document.getElementById("utr_number");
        section.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }
    if (!tos) {
      if (isMobile) {
        const section = document.getElementById("checkboxId");
        section.scrollIntoView({ behavior: "smooth" });
        setValidationRemider(true);
      }
      return;
    }
    dispatch(
      submitDeposit({
        paymentMethodId: modal.depositData?.paymentMethodId,
        amount: modal.depositData?.amount,
        utrReference: referenceNumber,
        bonusTemplateId: modal.depositData?.bonusTemplateId,
        screenshot: receipt,
      })
    );
  };

  useEffect(() => {
    if (payment.depositRequestedSuccessfully) {
      dispatch(closePaymentMethod());
      if (isMobile) dispatch(updateSelectedItem("depositCompleted"));
      else {
        dispatch(openPaymentCompletionModal("Deposit"));
        dispatch(closePaymentModal());
      }
    }
  }, [payment.depositRequestedSuccessfully, dispatch, isMobile]);
  useEffect(() => {
    if (isMobile)
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
  }, [payment.error, isMobile]);

  const copyId = async () => {
    const copyText = document.getElementById("id_upi").textContent;
    try {
      await navigator.clipboard.writeText(copyText);
      console.log("Content copied to clipboard");
      setCopyMessage("Copied");
      setTimeout(() => {
        setCopyMessage("");
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const copyItem = async (id) => {
    clearTimeout(timer.current);
    const copyText = document.getElementById(id)?.textContent || "";
    try {
      await navigator.clipboard.writeText(copyText);
      setCopyMessage(id);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
    timer.current = setTimeout(() => {
      setCopyMessage("");
    }, 1000);
  };

  const bankDetailRows = [
    {
      id: "acc_no",
      label: "Account No.",
      value: selectedMethodDetails.accountNo || "",
    },
    {
      id: "ifsc",
      label: "IFSC",
      value: selectedMethodDetails.ifscCode || "",
    },
    {
      id: "acc_name",
      label: "Account Name",
      value: selectedMethodDetails.accountName || "",
    },
    // {
    //   id: "b_name",
    //   label: "Bank Name",
    //   value: selectedMethodDetails.bankName || "",
    // },
  ];

  return (
    <div className="row upi--p">
      {payment.error && isMobile ? (
        <div className="w-100">
          <ErrorCard error={payment.error} />
        </div>
      ) : null}
      <div
        className="col-lg-6 col-md-6 col-xl-6 pt-4 pb-4 px-5 upi--p1"
        style={{
          borderRight: "0.5px solid #70707071",
          display: "flex",
          flexDirection: "column",
          justifyContent: isManualType ? "flex-start" : "center",
          alignItems: isManualType ? "stretch" : "center",
          position: isManualType ? "relative" : undefined,
        }}
      >
        <div
          className={
            isManualType
              ? "w-100"
              : "d-flex flex-column justify-content-center align-items-center w-100 h-100"
          }
        >
          {isMobile && !isManualType && (
            <>
              <div className="amount-info text-center">
                Please deposit{" "}
                <span style={{ color: "#F0C451" }}>
                  ₹ {modal.depositData.amount}/-
                </span>
              </div>
              <p className="text--normal">
                Scan the QR Code or you can send using below UPI Id
              </p>
            </>
          )}

          {isManualType ? (
            <>
              <p className="text--medium" style={{ color: "#EFC04B" }}>
                Bank Details
              </p>
              {bankDetailRows.map((row) => (
                <React.Fragment key={row.id}>
                  <label
                    className="text--normal"
                    style={{ color: "#E0E0E0" }}
                  >
                    {row.label}
                  </label>
                  <div className="d-flex w-100 mb-2">
                    <p className="mb-0 w-100 user-select-none" id={row.id}>
                      {row.value || "-"}
                    </p>
                    <div
                      style={{
                        float: "right",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      className="copy-btn copy-img"
                    >
                      <span
                        className={
                          copyMessage === row.id
                            ? "copied text-center text--small"
                            : "not-copied"
                        }
                      >
                        Copied
                      </span>
                      <button
                        type="button"
                        className="copy-info-btn"
                        onClick={() => copyItem(row.id)}
                      >
                        <img loading="lazy" src={copyIcon} alt="copy" />
                      </button>
                    </div>
                  </div>
                </React.Fragment>
              ))}
              {/* <p className="text--medium mt-3">
                <span style={{ opacity: "0.75" }}>How to know your</span>{" "}
                <b>UTR Number</b>
              </p> */}
            </>
          ) : showQr ? (
            <img
              loading="lazy"
              src={qrCodeUrl}
              alt="UPI QR code"
              onError={() => setQrFailed(true)}
              style={{
                width: isMobile ? "260px" : "360px",
                maxWidth: "90%",
                height: "auto",
                objectFit: "contain",
                background: "#fff",
                borderRadius: "8px",
                padding: "12px",
              }}
            />
          ) : (
            <div className="qr-unavailable text-center">
              <i className="bi bi-qr-code"></i>
              <p className="text--normal mb-1">QR code isn't available</p>
              <p className="text--small low-opacity mb-0">
                Send ₹{modal.depositData.amount} to the UPI ID shown here.
              </p>
            </div>
          )}
        </div>
        {/* {!isMobile && (
          <>
            <p className="text--medium mt-4">
              <span style={{ opacity: "0.75" }}>How to know your</span>{" "}
              <b>UTR Number</b>
            </p>
            <img  loading="lazy" src={gif} alt="gif" />
          </>
        )} */}
      </div>

      <div
        className="col-md-6 col-lg-6 col-xl-6 px-4 pt-3 pb-5 upi--p2"
        style={{ position: "relative" }}
      >
        {(!isMobile || isManualType) && (
          <>
            <div className="amount-info text-center">
              Please deposit{" "}
              <span style={{ color: "#F0C451" }}>
                ₹ {modal.depositData.amount}/-
              </span>
            </div>
            {!isManualType && (
              <p className="scan-qr-statement">
                {showQr
                  ? "Scan the QR Code or you can send using below UPI Id"
                  : "Send the amount using the UPI Id below"}
              </p>
            )}
          </>
        )}
        <form className="needs-validation">
          {!isManualType && (
            <div className="upi-id-div">
              <label className="text--medium text--light mb-2">
                Transfer your amount to this UPI Id:
              </label>
              <div className="d-flex upi_id">
                <p className="mb-0 text--medium" id="id_upi">
                  {upiId}
                </p>
                <button
                  type="button"
                  className="btn copy-btn"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-title="Tooltip on top"
                  onClick={copyId}
                >
                  <span className={copyMessage ? "copied" : "not-copied"}>
                    Copied
                    <div></div>
                  </span>
                  Copy
                </button>
              </div>
              {/* <div className="mt-2">
              <span className="text--medium">Name: </span>
              {isMobile ? <br /> : null}
              <span className="text--medium mb-0">John Doe Enterprise</span>
            </div> */}
            </div>
          )}

          <UtrReceiptFields
            referenceNumber={referenceNumber}
            onReferenceChange={(value) => {
              setReferenceNumber(value);
              setRedBorder(false);
            }}
            redBorder={redBorder}
            receipt={receipt}
            onReceiptChange={setReceipt}
            labelId="utr_number"
          />
          <p className="text--small text--light">
            Please pay exactly the amount stated above. The company is not
            responsible for any loss caused by paying a wrong amount or not
            completing the payment.
          </p>

          <div className="d-flex margin--bottom">
            <input
              className="form-check-input mt-0 checkbox-upi"
              type="checkbox"
              id="checkboxId"
              value=""
              aria-label="Checkbox for following text input"
              onChange={(e) => {
                setTos(e.target.checked);
                setValidationRemider(false);
              }}
              required
              style={
                checkValidation
                  ? { border: "1px solid red" }
                  : { border: "1px solid #707070" }
              }
            />
            <p
              className="text--medium text--light mx-2 checkbox-label-upi"
              style={{ marginTop: "3px" }}
            >
              I have read and agree with the terms of{" "}
              <span style={{ color: "#31B3FF" }}>
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => openInNewTab("/terms", e)}
                  style={{ textDecoration: "none", color: "#31B3FF" }}
                >
                  payment and withdrawal policy
                </a>
              </span>
            </p>
          </div>
          {payment.error && !isMobile ? (
            <ErrorCard error={payment.error} />
          ) : null}
          {/* <div style={{ marginBottom: "4.5rem" }}>
            {isMobile && (
              <>
                <p className="text--medium mt-2">
                  <span style={{ opacity: "0.75" }}>How to know your</span>{" "}
                  <b>UTR Number</b>
                </p>
                <img  loading="lazy" src={gif} className="mb-5 w-100" alt="gif" />
              </>
            )}
          </div> */}

          <ActionButtons
            toNext={goToNextStep}
            disabled={!canSubmit}
            label="Submit"
          />
        </form>
      </div>
    </div>
  );
}
